// App.jslogin
import React, { useCallback, useState, useEffect, useRef } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import AuthCallback from "./auth/callBack";
import SwitchPortal from "./components/public/SwitchPortal";

import {
  fetchAuthSession,
  signOut,
} from "@aws-amplify/auth";
import {
  clearAuthType,
  getPreferredAuthType,
} from "./auth/authTypeStorage";
import { resolveSessionAcrossPools } from "./auth/resolveSession";

import EmptyState from "./components/ui/EmptyState";
import { Film, Sparkles, Camera } from "lucide-react";

// Public Pages
import AuthorLandingPage from "./components/public/AuthorLandingPage";
import PublishingGroups from "./components/public/PublishingGroups";
import { PublishingGroupSignup, PublishingGroupPortal, PublishingGroupLogin } from "./components/public/PublishingGroupAccount";
import PublishingGroupHost from "./components/public/PublishingGroupHost";
import JoinPublishingGroup from "./components/public/JoinPublishingGroup";
import ReaderLanding from "./components/public/ReaderLanding";
import LandingPage from "./components/public/LandingPage";
import AboutPage from "./components/public/AboutPage";
import StripeRedirect from "./components/public/StripeRedirect";
import ThankYouPage from "./components/public/ThankYouPage";
import Homepage from "./components/public/Homepage";
import Market from "./components/public/Market";
import ProductPage from "./components/public/ProductPage";
import AuthorCartPage from "./components/public/AuthorCartPage";
import Newsletter from "./components/public/Newsletter";
import LoginAuthor from "./components/public/LoginAuthor";
import LoginReader from "./components/public/LoginReader";
import LoginChoice from "./components/public/LoginChoice";
import ForgotPassword from "./components/public/ForgotPassword";
import AuthorBlog from "./components/public/AuthorBlog";
import SignUpAuthor from "./components/public/SignUpAuthor";
import SignUpReader from "./components/public/SignupReader";
import Verify from "./components/public/Verify";
import AuthorShop from "./components/public/Bookstore";
import WelcomeReader from "./components/public/WelcomeReader";
import ReaderShopLayout from "./components/public/ReaderShopLayout";
import CustomPageView from "./components/public/CustomPageView";
import Footer from "./components/layout/Footer";
import LegalPage from "./components/public/LegalPage";
import ReaderAppreciationGuide from "./components/public/ReaderAppreciationGuide";
import PublicMetadata from "./components/public/PublicMetadata";


// Author Portal (private)
import AboutUs from "./components/authorportal/AboutUs";
import MainPage from "./components/authorportal/MainPage";
import NewsletterPage from "./components/authorportal/NewsletterPage";
import NewsletterComposer from "./components/newsletter/NewsletterComposer";
import Blog from "./components/authorportal/Blog";
import Settings from "./components/authorportal/Settings";
import Profile from "./components/authorportal/Profile";
import ProtectedRoute from "./components/authorportal/ProtectedRoute";
import AddBook from "./components/authorportal/AddBook";
import MyBooks from "./components/authorportal/MyBooks";
import MailCenter from "./components/authorportal/MailCenter";
import MailCenterPageUC from "./components/authorportal/MailCenterPageUC";
import LetsGetStarted from "./components/authorportal/LetsGetStarted";
import DomainClaim from "./components/authorportal/DomainClaim";
import StripeOnboarding from "./components/authorportal/StripeOnboarding";
import SubscribetoMembership from "./components/authorportal/SubscribetoMembership";
import BookDetails from "./components/authorportal/BookDetails";
import WelcomePage from "./components/authorportal/WelcomePage";

// Reader Portal //
import RMainPage from "./components/readerportal/RMainPage";
import AdminLogin from "./components/adminportal/AdminLogin";
import AdminLayout from "./components/adminportal/AdminLayout";
import AdminDashboard from "./components/adminportal/AdminDashboard";
import LadyLizaNewsletters from "./components/public/LadyLizaNewsletters";
import LadyLizaPage from "./components/public/LadyLizaPage";
import MonthlySpotlight from './components/public/MonthlySpotlight';
import AdminSocialDrafts from "./components/adminportal/AdminSocialDrafts";
import AdminMailCenter from "./components/adminportal/AdminMailCenter";
import AdminFeaturedBooks from "./components/adminportal/AdminFeaturedBooks";
import AdminPromotions from "./components/adminportal/AdminPromotions";
import AdminQuests from "./components/adminportal/AdminQuests";
import ProtectedAdminRoute from "./components/adminportal/ProtectedAdminRoute";

// ---------- host helpers ----------
const isAuthorHost = () => {
  const h = window.location.host.toLowerCase();
  const endsWithRoot = /\.bookishcircle\.com$/.test(h);
  const hasAtLeast3Labels = h.split(".").length > 2;
  const result = endsWithRoot && hasAtLeast3Labels;
  console.log("[HostCheck]", { host: h, isAuthorHost: result });
  return result;
};

function HostGateLayout() {
  return isAuthorHost() ? <PublishingGroupHost><ReaderShopLayout /></PublishingGroupHost> : <Outlet />;
}

function HostIndex({ onPortalSwitch }) {
  return isAuthorHost()
    ? <WelcomeReader />
    : <LandingPage onPortalSwitch={onPortalSwitch} />;
}


function AuthorHostRoute({ element }) {
  return isAuthorHost() ? element : <Navigate to="/" replace />;
}

// ---------- wrapper for protected portal routes ----------
function PrivateOutlet({ portalType }) {
  return (
    <ProtectedRoute portalType={portalType}>
      <Outlet />
    </ProtectedRoute>
  );
}


function PublicShell() {
  return (
    <div className="min-h-screen bg-bookish-cream flex flex-col">
      <div className="flex-grow">
        <Outlet />
      </div>
      <Footer />
    </div>
  );
}

// Added: keep signup as a full page route while supporting /signup?type=author entry flow.
function SignUpRouteResolver() {
  const location = useLocation();
  const signupType = new URLSearchParams(location.search).get("type");
  if (signupType === "publishing-group") return <PublishingGroupSignup />;

  return signupType === "author" ? <SignUpAuthor /> : <SignUpReader />;
}


function App() {

  const [user, setUser] = useState(null);
  const logoutTimerRef = useRef(null);
  const lastResetRef = useRef(Date.now());

  const resolveActiveSession = useCallback(async () => {
    if (window.location.pathname.startsWith("/admin")) return null;
    const session = await resolveSessionAcrossPools();

    if (!session) {
      setUser(null);
      return null;
    }

    setUser(session.user);
    return session.user;
  }, []);

  const handlePortalSwitch = (targetType) => {
    if (!['author', 'reader'].includes(targetType)) return;
    const from = window.location.pathname + window.location.search + window.location.hash;
    window.location.href = '/switchportal?' + new URLSearchParams({ type: targetType, from });
  };
  const startInactivityTimer = useCallback(() => {
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    logoutTimerRef.current = setTimeout(async () => {
      if (window.location.pathname.startsWith("/admin")) return;
      try {
        const redirectType = getPreferredAuthType();
        const session = await fetchAuthSession();
        const isExpired = !session.tokens?.accessToken;
        if (isExpired) {
          await signOut();
          sessionStorage.clear();
          localStorage.removeItem("user");
          clearAuthType();
          setUser(null);
          alert("You've been logged out due to 30 minutes of inactivity.");
          window.location.href = redirectType === "reader" ? "/loginreader" : "/loginauthor";
        } else {
          startInactivityTimer();
        }
      } catch (error) {
        const redirectType = getPreferredAuthType();
        await signOut();
        sessionStorage.clear();
        localStorage.removeItem("user");
        clearAuthType();
        setUser(null);
        alert("Session check failed. You've been logged out.");
        window.location.href = redirectType === "reader" ? "/loginreader" : "/loginauthor";
      }
    }, 30 * 60 * 1000);
  }, [setUser]);

  useEffect(() => {
    if (!user || getPreferredAuthType() === "reader") return;
    const resetTimer = () => {
      const now = Date.now();
      if (now - lastResetRef.current > 5000) {
        startInactivityTimer();
        lastResetRef.current = now;
      }
    };
    const events = ["mousemove", "keydown", "click", "touchstart"];
    events.forEach((e) => window.addEventListener(e, resetTimer));
    startInactivityTimer();
    return () => {
      events.forEach((e) => window.removeEventListener(e, resetTimer));
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    };
  }, [user]);
  useEffect(() => {
    resolveActiveSession();
  }, [resolveActiveSession]);

  return (
    <Router>
      <PublicMetadata />
      <Routes>
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/switchportal" element={<SwitchPortal setUser={setUser} />} />
        <Route element={<PublicShell />}>
          <Route path="/" element={<HostGateLayout />}>
            <Route index element={<HostIndex onPortalSwitch={handlePortalSwitch} />} />

            {/* Public reader sub-pages */}
            <Route path="bookstore" element={<AuthorHostRoute element={<AuthorShop />} />} />
            <Route path="authors" element={isAuthorHost() ? <CustomPageView /> : <AuthorLandingPage onPortalSwitch={handlePortalSwitch} />} />
            <Route path="authorblog" element={<AuthorHostRoute element={<AuthorBlog />} />} />
            <Route path="newsletter" element={<AuthorHostRoute element={<Newsletter />} />} />
            <Route path="contact" element={<AuthorHostRoute element={<AboutPage />} />} />
            <Route path="extras" element={<AuthorHostRoute element={<EmptyState icon={Sparkles} title="Extras" description="Extra content coming soon." />} />} />
            <Route path="behind-the-scenes" element={<AuthorHostRoute element={<EmptyState icon={Camera} title="Behind the Scenes" description="Behind the scenes content coming soon." />} />} />
            <Route path="audio-video" element={<AuthorHostRoute element={<EmptyState icon={Film} title="Audio / Video" description="Audio and video content coming soon." />} />} />
            <Route path="shop/:bookslug" element={<AuthorHostRoute element={<ProductPage />} />} />
            <Route path="cart" element={<AuthorHostRoute element={<AuthorCartPage />} />} />
            <Route path="thank-you" element={<ThankYouPage />} />
            <Route path=":customPageSlug" element={<AuthorHostRoute element={<CustomPageView />} />} />
            
          </Route>
        
          {/* Other public apex routes */}
          <Route path="/welcomepage" element={<ProtectedRoute portalType="author"><WelcomePage /></ProtectedRoute>} />
          <Route path="/welcome" element={<Homepage />} />
          <Route path="/market" element={<Market />} />
          <Route path="/shop/:bookslug" element={<ProductPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/reader-appreciation" element={<ReaderAppreciationGuide />} />
          <Route path="/privacy" element={<LegalPage type="privacy" />} />
          <Route path="/terms" element={<LegalPage type="terms" />} />
          <Route path="/verify" element={<Verify />} />
          <Route path="/stripe-return" element={<StripeRedirect />} />
          <Route path="/thank-you" element={<ThankYouPage />} />
          <Route path="/loginauthor" element={<LoginAuthor setUser={setUser} />} />
          <Route path="/loginreader" element={<LoginReader setUser={setUser} />} />
          <Route path="/login" element={<LoginChoice />} />
          <Route path="/lady-liza/newsletters" element={<LadyLizaNewsletters />} />
          <Route path="/lady-liza" element={<LadyLizaPage />} />
          <Route path="/lady-liza/spotlight" element={<MonthlySpotlight standalone />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/forgotpassword" element={<ForgotPassword />} />
          <Route path="/signupauthor" element={<SignUpAuthor />} />
          <Route path="/publishing-groups" element={<PublishingGroups />} />
          <Route path="/publishing-groups/signup" element={<PublishingGroupSignup />} />
          <Route path="/publishing-groups/login" element={<PublishingGroupLogin />} />
          <Route path="/publishing-groups/join" element={<JoinPublishingGroup />} />
          <Route path="/publishing-groups/author-membership" element={<JoinPublishingGroup membershipOnly />} />
          <Route path="/publishing-group-portal" element={<PublishingGroupPortal />} />
          <Route path="/signupreader" element={<SignUpReader />} />
          {/* Added: public full-page signup route used by author funnel CTAs. */}
          <Route path="/signup" element={<SignUpRouteResolver />} />
          <Route path="/landing" element={<LandingPage onPortalSwitch={handlePortalSwitch} />} />
          {/* Added: keep /landingpage available as requested, without removing existing /landing. */}
          <Route path="/landingpage" element={<LandingPage onPortalSwitch={handlePortalSwitch} />} />
          <Route path="/readerlanding" element={<ReaderLanding />} />
        </Route>
          {/* AUTHOR PORTAL (private, no top layout) */}
          <Route path="/authorportal" element={<PrivateOutlet portalType="author" />}>
            <Route path="mailcenter" element={<MailCenter />} />
            <Route path="/authorportal/mailcenterpageuc" element={<MailCenterPageUC />} />

            <Route index element={<MainPage />} />
            <Route path="mainpage" element={<MainPage />} />
            <Route path="addbook" element={<AddBook />} />
            <Route path="blog" element={<Blog />} />
            <Route path="newsletterpage" element={<NewsletterPage />} />
            <Route path="newslettercomposer" element={<NewsletterComposer />} />
            <Route path="analytics" element={<Navigate to="/authorportal/mainpage?tab=home" replace />} />
            <Route path="settings" element={<Settings />} />
            <Route path="profile" element={<Profile />} />
            <Route path="mybooks" element={<MyBooks />} />
            <Route path="aboutus" element={<AboutUs />} />
            <Route path="letsgetstarted" element={<LetsGetStarted />} />
            <Route path="domainclaim" element={<DomainClaim standalone />} />
            <Route path="stripeonboarding" element={<StripeOnboarding />} />
            <Route path="subscribetomembership" element={<SubscribetoMembership />} />
            <Route path="bookdetails" element={<BookDetails />} />
          </Route>
          <Route path="/admin" element={<ProtectedAdminRoute><AdminLayout /></ProtectedAdminRoute>}>
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="social-drafts" element={<AdminSocialDrafts />} />
            <Route path="mail-center" element={<AdminMailCenter />} />
            <Route path="featured-books" element={<AdminFeaturedBooks />} />
            <Route path="promotions" element={<AdminPromotions />} />
            <Route path="quests" element={<AdminQuests />} />
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
          </Route>
          {/* READER PORTAL (private) */}
          <Route path="/readerportal" element={<PrivateOutlet portalType="reader" />}>
            <Route index element={<RMainPage />} />
            <Route path="mainpage" element={<RMainPage />} />
          </Route>
  
          </Routes>
    </Router>
  );
  
}

export default App;
